import { ButtonView, Command, type Editor, Plugin } from 'ckeditor5';
import i18next from 'i18next';
import ckCss from 'resources/ckeditor5-content.css?inline';
import { CustomPlugin } from '@/annotation/ckEditorPlugins/CKEditorPlugins.constants';
import icon from '@/annotation/ckEditorPlugins/ckIcons/ckeditor5-seeq-copy.svg?raw';
import { PluginDependencies } from '@/annotation/ckEditorPlugins/plugins/PluginDependencies';
import { convertContentHslToHex, convertImagesSrc } from '@/core/html.utilities';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { errorToast, infoToast, warnToast } from '@/utilities/toast.utilities';
import type { BasePluginDependencies } from '@/annotation/ckEditorPlugins/CKEditorPlugins.constants';

const juiceAsync = () => import('juice');
type Juice = (html: string, options: any) => string;
let juice: Juice | undefined;

export const COMMAND_NAME = 'copy_for_external';

export const convertStyleToInline = (juiceConverter: Juice, input: string, styleToUse: string): string => {
  // <style data-cke="true">...</style>
  const juiceOptions = {
    extraCss: styleToUse,
    preserveMediaQueries: false,
    preserveKeyFrames: false,
    xmlMode: true,
  };
  return juiceConverter(input, juiceOptions);
};

export const getCKEditorCssStyle = (): string => {
  return ckCss;
};

/** Plain text for clipboard alongside HTML (email clients often use text/plain). */
function clipboardPlainTextFromHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body?.innerText ?? '';
}

/**
 * Full pipeline for "copy for external": inline images, juice + CK CSS, HSL→hex wrapper.
 * Caller must ensure global `juice` is loaded first.
 */
export function applyCopyForEmailHtmlTransforms(rawHtml: string): string {
  let processedData = convertImagesSrc(rawHtml);
  const contentPreJuice = `<div class="ck-content" data-notallowpaste="true">${processedData}</div>`;
  return convertContentHslToHex(convertStyleToInline(juice!, contentPreJuice, getCKEditorCssStyle()));
}

/**
 * When the Async Clipboard API is unavailable (JSDOM, locked-down contexts), copy rich HTML using a temporary
 * contenteditable host so execCommand('copy') can still run (main report surface may be contenteditable=false).
 */
function copyHtmlViaTemporaryEditable(html: string): void {
  const host = document.createElement('div');
  host.setAttribute('contenteditable', 'true');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.innerHTML = html;
  document.body.appendChild(host);

  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(host);
  selection?.removeAllRanges();
  selection?.addRange(range);

  const ok = document.execCommand('copy');
  selection?.removeAllRanges();
  document.body.removeChild(host);

  if (!ok) {
    throw new Error('execCommand("copy") failed for copy-for-external fallback');
  }
}

async function writeEmailHtmlToClipboard(html: string, plain: string): Promise<void> {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ]);
  } catch {
    copyHtmlViaTemporaryEditable(html);
  }
}

async function copyEditorDataForEmailReadOnly(editor: Editor, plugin: CopyForEmail): Promise<void> {
  try {
    if (!juice) {
      juice = (await juiceAsync()).default;
    }
    const rawHtml = editor.getData();
    const html = applyCopyForEmailHtmlTransforms(rawHtml);
    const plain = clipboardPlainTextFromHtml(html);
    await writeEmailHtmlToClipboard(html, plain);
    infoToast({ messageKey: 'REPORT.EDITOR.COPY_FOR_EMAIL_SUCCESS' });
  } catch (err) {
    logError(formatMessage`Copy for email failed: ${err}`);
    errorToast({ httpResponseOrError: err });
  } finally {
    plugin.shouldCopyForEmail = false;
  }
}

export class CopyForEmail extends Plugin {
  static pluginName = CustomPlugin.CopyForEmail;
  static setup = {
    name: CopyForEmail.pluginName,
    plugin: CopyForEmail,
    toolbar: CopyForEmail.pluginName,
  };
  shouldCopyForEmail = false;
  deps: BasePluginDependencies | undefined;

  init() {
    this.deps = this.editor.config.get(PluginDependencies.pluginName) as BasePluginDependencies;
    this.defineToolbarButton();
    this.defineClipboardHandlers();

    this.editor.commands.add(COMMAND_NAME, new CopyForEmailCommand(this.editor));
  }

  defineToolbarButton() {
    const editor = this.editor;

    editor.ui.componentFactory.add(CopyForEmail.pluginName, (locale) => {
      const view = new ButtonView(locale);

      view.set({
        label: i18next.t('REPORT.EDITOR.COPY_FOR_EMAIL'),
        icon,
        tooltip: true,
        class: 'copyForExternalPaste',
      });
      view.on('execute', () => {
        this.shouldCopyForEmail = true;
        editor.execute(COMMAND_NAME);
      });
      return view;
    });
  }

  defineClipboardHandlers() {
    this.editor.editing.view.document.on(
      'clipboardOutput',
      (evt, data) => {
        const clipboardData = data.dataTransfer.getData('text/html');
        const isForEmail = this.shouldCopyForEmail;
        const processedData = isForEmail
          ? applyCopyForEmailHtmlTransforms(clipboardData)
          : convertImagesSrc(clipboardData);

        data.dataTransfer.setData('text/html', processedData);
        if (isForEmail) {
          infoToast({ messageKey: 'REPORT.EDITOR.COPY_FOR_EMAIL_SUCCESS' });
          evt.stop();
        }
        this.shouldCopyForEmail = false;
      },
      { priority: 'lowest' },
    );

    this.editor.editing.view.document.on(
      'clipboardInput',
      (evt, data) => {
        if (this.editor.isReadOnly) {
          return;
        }
        const dataTransfer = data.dataTransfer;
        const content = dataTransfer.getData('text/html');
        if (content.indexOf('data-notallowpaste="true"') !== -1) {
          warnToast({ messageKey: 'REPORT.EDITOR.NO_PASTE_FOR_EMAIL' });
          evt.stop();
        }
      },
      { priority: 'highest' },
    );
  }
}

export class CopyForEmailCommand extends Command {
  constructor(editor: Editor) {
    super(editor);
    // Copy does not change document model; allow in read-only (Organizer topic view).
    this.affectsData = false;
  }

  execute() {
    const editor = this.editor;
    const plugin = editor.plugins.get(CopyForEmail.pluginName) as CopyForEmail;

    const ui = editor.ui as { getEditableElement?: () => HTMLElement | null };
    const editable = ui.getEditableElement?.() ?? null;
    const domNotUserEditable = editable?.getAttribute('contenteditable') === 'false';

    // Read-only or contenteditable=false: execCommand('copy') does not copy the document; use getData() + Clipboard API.
    if (plugin.shouldCopyForEmail && (editor.isReadOnly || domNotUserEditable)) {
      return copyEditorDataForEmailReadOnly(editor, plugin);
    }

    const model = editor.model;
    const firstPosition = model.document.selection.getFirstPosition();
    const lastPosition = model.document.selection.getLastPosition();
    if (firstPosition && lastPosition && firstPosition.compareWith(lastPosition) === 'same') {
      model.change((writer) => {
        const root = model.document.getRoot()!;
        writer.setSelection(writer.createRangeIn(root));
      });
    }
    if (!juice) {
      juiceAsync().then((module) => {
        juice = module.default;
        window.document.execCommand('copy');
      });
    } else {
      window.document.execCommand('copy');
    }
  }
}
